import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EOrderType } from '@aqx-core/common/api/order';
import { i18n } from '@aqx-core/common/i18n';

import { decimals, priceToPoints } from '../../../../calculation/order';
import { IValidatedPricePointsResult, IValidationResult } from './types';
import { calculatePointsFromPrice, calculatePriceFromPoints } from './utils';

export const validateStopLossPoints = (stopLossValue: number, stopsLevel: number): IValidationResult => {
  if (stopLossValue < stopsLevel) {
    return {
      isValid: false,
      message: i18n.t('Stop loss required: Minimum of {{stopsLevel}} points', { stopsLevel })
    };
  }
  return { isValid: true };
};

/**
 * Check if order type is a buy order (including pending buy orders)
 */
const isBuyOrder = (orderType: EOrderType): boolean => {
  return orderType === EOrderType.BUY || orderType === EOrderType.BUY_STOP || orderType === EOrderType.BUY_LIMIT;
};

/**
 * Validate stop loss price direction for buy orders
 */
const validateBuyOrderStopLoss = (
  stopLossValue: number,
  entryPrice: number,
  pointStep: number
): { isValid: boolean; points?: number; message?: string } => {
  if (stopLossValue >= entryPrice) {
    return {
      isValid: false,
      message: i18n.t('Stop loss must be below entry price for buy orders')
    };
  }

  const points = priceToPoints({
    input: entryPrice,
    price: stopLossValue,
    pointStep
  });

  return { isValid: true, points };
};

/**
 * Validate stop loss price direction for sell orders
 */
const validateSellOrderStopLoss = (
  stopLossValue: number,
  entryPrice: number,
  pointStep: number
): { isValid: boolean; points?: number; message?: string } => {
  if (stopLossValue <= entryPrice) {
    return {
      isValid: false,
      message: i18n.t('Stop loss must be above entry price for sell orders')
    };
  }

  const points = priceToPoints({
    input: stopLossValue,
    price: entryPrice,
    pointStep
  });

  return { isValid: true, points };
};

/**
 * Validate stop loss price based on web-trader business logic
 */
export const validateStopLossPrice = (params: {
  stopLossValue: number;
  price?: string;
  orderType: EOrderType;
  pointStep: number;
  stopsLevel: number;
}): IValidationResult => {
  const { stopLossValue, price, orderType, pointStep, stopsLevel } = params;

  if (!price) {
    return { isValid: false, message: i18n.t('Entry price is required') };
  }

  const entryPrice = parseFloat(price);
  if (!entryPrice) {
    return { isValid: false, message: i18n.t('Invalid entry price') };
  }

  // Validate price direction and calculate points based on order type
  const validation = isBuyOrder(orderType)
    ? validateBuyOrderStopLoss(stopLossValue, entryPrice, pointStep)
    : validateSellOrderStopLoss(stopLossValue, entryPrice, pointStep);

  if (!validation.isValid) {
    return { isValid: false, message: validation.message };
  }

  if (validation.points === undefined || validation.points < stopsLevel) {
    return {
      isValid: false,
      message: i18n.t('Stop loss required: Minimum of {{stopsLevel}} points from {{price}}', {
        stopsLevel,
        price: entryPrice
      })
    };
  }

  return { isValid: true };
};

/**
 * Helper: Validate take profit price direction
 */
const validateTakeProfitPriceDirection = (
  takeProfitValue: number,
  entryPrice: number,
  orderType: EOrderType
): IValidationResult => {
  if (isBuyOrder(orderType)) {
    if (takeProfitValue <= entryPrice) {
      return {
        isValid: false,
        message: i18n.t('Take profit must be above entry price for buy orders')
      };
    }
  } else {
    if (takeProfitValue >= entryPrice) {
      return {
        isValid: false,
        message: i18n.t('Take profit must be below entry price for sell orders')
      };
    }
  }
  return { isValid: true };
};

/**
 * Helper: Calculate take profit points using web-trader patterns
 */
const calculateTakeProfitPoints = (
  takeProfitValue: number,
  entryPrice: number,
  orderType: EOrderType,
  pointStep: number
): number | undefined => {
  if (isBuyOrder(orderType)) {
    return priceToPoints({
      input: takeProfitValue,
      price: entryPrice,
      pointStep
    });
  } else {
    return priceToPoints({
      input: entryPrice,
      price: takeProfitValue,
      pointStep
    });
  }
};

/**
 * Validate take profit price based on web-trader business logic
 */
export const validateTakeProfitPrice = (params: {
  takeProfitValue: number;
  price?: string;
  orderType: EOrderType;
  pointStep: number;
  stopsLevel?: number;
}): IValidationResult => {
  const { takeProfitValue, price, orderType, pointStep, stopsLevel = 10 } = params;

  if (!price) {
    return { isValid: false, message: i18n.t('Entry price is required') };
  }

  const entryPrice = parseFloat(price);
  if (!entryPrice) {
    return { isValid: false, message: i18n.t('Invalid entry price') };
  }

  // Validate price direction
  const directionResult = validateTakeProfitPriceDirection(takeProfitValue, entryPrice, orderType);
  if (!directionResult.isValid) {
    return directionResult;
  }

  // Calculate points using web-trader pattern
  const calculatedPoints = calculateTakeProfitPoints(takeProfitValue, entryPrice, orderType, pointStep);

  if (calculatedPoints === undefined || calculatedPoints < stopsLevel) {
    return {
      isValid: false,
      message: i18n.t('Take profit required minimum of {{stopsLevel}} points from entry price', {
        stopsLevel
      })
    };
  }

  return { isValid: true };
};

/**
 * Comprehensive stop loss validation
 */
export const validateStopLoss = (params: {
  stopLoss?: string;
  price?: string;
  indicate: 'POINTS' | 'PRICE';
  symbolDetail?: Partial<ISymbolDetail>;
  orderType?: EOrderType;
}): IValidationResult => {
  const { stopLoss, indicate, price, symbolDetail, orderType } = params;

  if (!stopLoss) {
    return { isValid: true };
  }

  const stopLossValue = parseFloat(stopLoss);
  if (!symbolDetail || orderType === null || orderType === undefined) {
    return { isValid: true };
  }

  const { pointStep = 0.0001, stopsLevel = 10 } = symbolDetail;

  // Check for negative values
  if (stopLossValue <= 0) {
    return { isValid: false, message: i18n.t('Stop loss must be greater than 0') };
  }

  // Check decimal precision for points
  if (indicate === 'POINTS' && decimals(stopLossValue) > 0) {
    return { isValid: false, message: i18n.t('Points must be whole numbers') };
  }

  // Check minimum stops level
  if (indicate === 'POINTS') {
    return validateStopLossPoints(stopLossValue, stopsLevel);
  } else {
    return validateStopLossPrice({
      stopLossValue,
      price,
      orderType,
      pointStep,
      stopsLevel
    });
  }
};

/**
 * Comprehensive take profit validation
 */
// eslint-disable-next-line complexity
export const validateTakeProfit = (params: {
  takeProfit?: string;
  indicate: 'POINTS' | 'PRICE';
  price?: string;
  symbolDetail?: Partial<ISymbolDetail>;
  orderType?: EOrderType;
}): IValidationResult => {
  const { takeProfit, indicate, price, symbolDetail, orderType } = params;

  if (!takeProfit) {
    return { isValid: true };
  }

  const takeProfitValue = parseFloat(takeProfit);

  // Check for negative values
  if (takeProfitValue <= 0) {
    return { isValid: false, message: i18n.t('Take profit must be greater than 0') };
  }

  // Check decimal precision for points
  if (indicate === 'POINTS' && decimals(takeProfitValue) > 0) {
    return { isValid: false, message: i18n.t('Points must be whole numbers') };
  }

  // Take profit minimum is stopsLevel (same as stop loss)
  if (indicate === 'POINTS') {
    const stopsLevel = symbolDetail?.stopsLevel || 10;
    if (takeProfitValue < stopsLevel) {
      return {
        isValid: false,
        message: i18n.t('Take profit required minimum of {{stopsLevel}} points', { stopsLevel })
      };
    }
  }

  // For price validation, use the validateTakeProfitPrice method
  if (indicate === 'PRICE' && orderType !== null && orderType !== undefined && symbolDetail) {
    const { pointStep = 0.0001, stopsLevel = 10 } = symbolDetail;
    return validateTakeProfitPrice({
      takeProfitValue,
      price,
      orderType,
      pointStep,
      stopsLevel
    });
  }

  return { isValid: true };
};

/**
 * Validate and normalize stop loss price with threshold checking
 */
export const validateAndNormalizeStopLossPrice = (params: {
  price: string;
  entryPrice: string;
  orderType: EOrderType;
  pointStep: number;
  symbolDetail?: Partial<ISymbolDetail>;
}): IValidatedPricePointsResult | null => {
  const { price, entryPrice, orderType, pointStep, symbolDetail } = params;

  const priceValue = parseFloat(price);
  const entryPriceValue = parseFloat(entryPrice);

  if (!priceValue || !entryPriceValue || !pointStep) {
    return null;
  }

  // Validate the price first
  const validation = validateStopLossPrice({
    stopLossValue: priceValue,
    price: entryPrice,
    orderType,
    pointStep,
    stopsLevel: symbolDetail?.stopsLevel || 10
  });

  if (!validation.isValid) {
    return {
      price,
      points: '0',
      isValid: false,
      message: validation.message
    };
  }

  // Calculate points
  const buySell = orderType === EOrderType.BUY ? 'BUY' : 'SELL';
  const points = calculatePointsFromPrice({
    price,
    type: 'SL',
    entryPrice,
    buySell,
    pointStep
  });

  const normalizedPrice = symbolDetail?.decimal ? priceValue.toFixed(symbolDetail.decimal) : price;

  return {
    price: normalizedPrice,
    points: points || '0',
    isValid: true
  };
};

/**
 * Validate and normalize stop loss points with threshold checking
 */
export const validateAndNormalizeStopLossPoints = (params: {
  points: string;
  entryPrice: string;
  orderType: EOrderType;
  pointStep: number;
  symbolDetail: Partial<ISymbolDetail> | undefined;
}): IValidatedPricePointsResult | null => {
  const { points, entryPrice, orderType, pointStep, symbolDetail } = params;

  const pointsValue = parseFloat(points);
  const entryPriceValue = parseFloat(entryPrice);

  if (!pointsValue || !entryPriceValue || !pointStep) {
    return null;
  }

  // Validate the points first
  const stopsLevel = symbolDetail?.stopsLevel || 10;
  const validation = validateStopLossPoints(pointsValue, stopsLevel);

  if (!validation.isValid) {
    return {
      price: '0',
      points,
      isValid: false,
      message: validation.message
    };
  }

  // Calculate price
  const buySell = orderType === EOrderType.BUY ? 'BUY' : 'SELL';
  const price = calculatePriceFromPoints({
    points,
    type: 'SL',
    entryPrice,
    buySell,
    pointStep,
    symbolDetail
  });

  const normalizedPoints = Math.floor(pointsValue).toString();

  return {
    price: price || '0',
    points: normalizedPoints,
    isValid: true
  };
};

/**
 * Validate and normalize take profit price with threshold checking
 */
export const validateAndNormalizeTakeProfitPrice = (params: {
  price: string;
  entryPrice: string;
  orderType: EOrderType;
  pointStep: number;
  symbolDetail?: Partial<ISymbolDetail>;
}): IValidatedPricePointsResult | null => {
  const { price, entryPrice, orderType, pointStep, symbolDetail } = params;

  const priceValue = parseFloat(price);
  const entryPriceValue = parseFloat(entryPrice);

  if (!priceValue || !entryPriceValue || !pointStep) {
    return null;
  }

  // Validate the price first
  const validation = validateTakeProfitPrice({
    takeProfitValue: priceValue,
    price: entryPrice,
    orderType,
    pointStep,
    stopsLevel: symbolDetail?.stopsLevel || 10
  });

  if (!validation.isValid) {
    return {
      price,
      points: '0',
      isValid: false,
      message: validation.message
    };
  }

  // Calculate points
  const buySell = orderType === EOrderType.BUY ? 'BUY' : 'SELL';
  const points = calculatePointsFromPrice({
    price,
    type: 'TP',
    entryPrice,
    buySell,
    pointStep
  });

  const normalizedPrice = symbolDetail?.decimal ? priceValue.toFixed(symbolDetail.decimal) : price;

  return {
    price: normalizedPrice,
    points: points || '0',
    isValid: true
  };
};

/**
 * Validate and normalize take profit points with threshold checking
 */
export const validateAndNormalizeTakeProfitPoints = (params: {
  points: string;
  entryPrice: string;
  orderType: EOrderType;
  pointStep: number;
  symbolDetail: Partial<ISymbolDetail> | undefined;
}): IValidatedPricePointsResult | null => {
  const { points, entryPrice, orderType, pointStep, symbolDetail } = params;

  const pointsValue = parseFloat(points);
  const entryPriceValue = parseFloat(entryPrice);

  if (!pointsValue || !entryPriceValue || !pointStep) {
    return null;
  }

  // Validate the points first (minimum stopsLevel for take profit)
  const stopsLevel = symbolDetail?.stopsLevel || 10;
  if (pointsValue < stopsLevel) {
    return {
      price: '0',
      points,
      isValid: false,
      message: i18n.t('Take profit required minimum of {{stopsLevel}} points', { stopsLevel })
    };
  }

  // Calculate price
  const buySell = orderType === EOrderType.BUY ? 'BUY' : 'SELL';
  const price = calculatePriceFromPoints({
    points,
    type: 'TP',
    entryPrice,
    buySell,
    pointStep,
    symbolDetail
  });

  const normalizedPoints = Math.floor(pointsValue).toString();

  return {
    price: price || '0',
    points: normalizedPoints,
    isValid: true
  };
};
